/* eslint-disable sonarjs/no-use-of-empty-return-value */
import groupBy from 'lodash/groupBy';
import { useEffect } from 'react';

import { useEnergyOptions } from '~src/context/EnergyOptionsContext';
import { usePublicCalculatorContext } from '~src/context/PublicCalculatorContext';
import { DeepPartial, HeatPumpOutdoorUnit, Residence } from '~src/model';
import {
  calculateHeatPumpScores,
  calculateResidenceHeatingRequired,
  findBestCompatibleIndoorUnit,
  isResidenceFilled,
} from '~src/util';

const QUIET_DB = 58;
const BEST_DESIGN_BRAND = 'Toshiba';

export const useProductInformationOptions = () => {
  const { residence, outdoorUnits, indoorUnits, offerProducts, setOfferProducts } = usePublicCalculatorContext();

  const { bestPrice, bestSound, bestDesign } = categoriseHeatPumps(residence, outdoorUnits);

  const { heatPumpOutdoorUnit } = offerProducts ?? {};

  const setOutdoorUnit = (newUnit?: HeatPumpOutdoorUnit) => {
    const heatPumpIndoorUnit = findBestCompatibleIndoorUnit(newUnit, indoorUnits);

    if (!heatPumpIndoorUnit || !newUnit) {
      setOfferProducts?.({});
      return;
    }

    setOfferProducts?.({ heatPumpOutdoorUnit: newUnit, heatPumpIndoorUnit });
  };

  useEffect(() => {
    if (!heatPumpOutdoorUnit || ![bestPrice?.id, bestSound?.id, bestDesign?.id].includes(heatPumpOutdoorUnit.id)) {
      const unit = bestPrice ?? bestSound ?? bestDesign;

      setOutdoorUnit(unit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bestPrice]);

  return [
    ...(bestPrice ? [{ label: 'Bedste Pris', value: bestPrice.id, onChange: setOutdoorUnit }] : []),
    ...(bestSound ? [{ label: 'Støjsvag', value: bestSound.id, onChange: setOutdoorUnit }] : []),
    ...(bestDesign ? [{ label: 'Bedste design', value: bestDesign.id, onChange: setOutdoorUnit }] : []),
  ];
};

const categoriseHeatPumps = (
  residence?: DeepPartial<Residence>,
  outdoorUnits: HeatPumpOutdoorUnit[] = []
): { bestPrice?: HeatPumpOutdoorUnit; bestSound?: HeatPumpOutdoorUnit; bestDesign?: HeatPumpOutdoorUnit } => {
  if (!residence || !isResidenceFilled(residence)) {
    return {};
  }

  const heatingRequired = calculateResidenceHeatingRequired(residence);

  if (!heatingRequired) {
    return {};
  }

  const { available: bestPrices, bestUnavailable } = calculateHeatPumpScores(
    outdoorUnits,
    { ...residence, noiseWeight: 'small' } as Residence,
    heatingRequired
  );

  const { available: bestSounds } = calculateHeatPumpScores(
    outdoorUnits,
    { ...residence, noiseWeight: 'large' } as Residence,
    heatingRequired
  );

  const bestSound =
    bestSounds[0]?.id === bestPrices[0]?.id
      ? bestSounds
          .slice(1)
          .find(({ noiseLevel }) => (noiseLevel ? noiseLevel < (bestSounds[0].noiseLevel ?? QUIET_DB) : false))
      : bestSounds[0];

  const bestPrice = bestPrices[0] ?? bestUnavailable;

  const bestDesign =
    bestPrice && bestSound
      ? bestPrices.find(({ brand, id }) => brand === BEST_DESIGN_BRAND && ![bestPrice?.id, bestSound?.id].includes(id))
      : undefined;

  return { bestPrice, bestSound, bestDesign };
};

export const useCheapestOutdoorUnitPerBrand = (
  inputResidence?: DeepPartial<Residence>,
  inputOutdoorUnits?: HeatPumpOutdoorUnit[]
) => {
  const { residence: contextResidence, outdoorUnits: contextOutdoorUnits } = usePublicCalculatorContext();
  const energyOptions = useEnergyOptions();

  const residence = inputResidence ?? contextResidence;
  const outdoorUnits = inputOutdoorUnits ?? contextOutdoorUnits;

  if (!residence || !isResidenceFilled(residence)) {
    return [];
  }

  const heatingRequired = calculateResidenceHeatingRequired(residence, energyOptions);

  if (!heatingRequired) {
    return [];
  }

  const { available: bestHeatPumps } = calculateHeatPumpScores(
    outdoorUnits ?? [],
    { ...residence, noiseWeight: 'small' } as Residence,
    heatingRequired
  );

  const sortedByPrice = bestHeatPumps.sort((a, b) => a.price - b.price);

  return Object.values(groupBy(sortedByPrice, 'brand')).map(item => item[0]);
};

export const getHeatPumpTag = (index: number, noiseLevel?: number) => {
  const label = (noiseLevel ?? QUIET_DB) < QUIET_DB ? 'Støjsvag' : undefined;

  return index === 0 ? 'Bedste pris' : label;
};

type WithModel = { model: string };

export const getHeatPumpKey = (indoorUnit?: WithModel, outdoorUnit?: WithModel) =>
  `${indoorUnit?.model}-${outdoorUnit?.model}`;
